import { useState, FC, useEffect } from 'react';
import { translate } from 'translations';
import { AccordionTable, CollapseTableData } from 'learning-panel-components';
import InnerTableContainer from '../leaderboard/innerTableContainer';
import { getRow, TeamData } from '../leaderboard/challengeTeamLeaderboard';
import styles from './emptyLeaderboard.module.scss';

const TeamLeaderboard: FC<TeamLeaderboardProps> = (props: TeamLeaderboardProps) => {
    const { teams, myTeams, onLoadMore, hasMore, teamsUsersOpenState, onChangeOpenMode } = props;

    const [allTeamsRows, setAllTeamsRows] = useState<CollapseTableData[]>([]);
    const [myTeamsRows, setMyTeamsRows] = useState<CollapseTableData[]>([]);
    const [myTeamsIsOpen, setMyTeamsIsOpen] = useState<boolean>(true);
    const [allTeamsIsOpen, setAllTeamsIsOpen] = useState<boolean>(true);
    const [isPreparingData, setIsPreparingData] = useState<boolean>(true);
    const membersNumberText = translate('members');
    const myTeamsText = translate('myTeams');
    const allTeamsText = translate('allTeams');
    const headersText = [translate('Rank'), translate('Team'), translate('Members'), translate('DevCoins')];
    const headers = headersText.map((text, i) => <div key={i}>{text}</div>);
    const outOfText = translate('outOf');
    const tooltipText = translate('membersAllChallengesTooltip');

    const mapTeam = (teams: TeamData[], teamType: string) =>
        teams?.map((team: TeamData, i: number) => ({
            row: getRow(
                {
                    rank: team.rank,
                    name: team.teamName,
                    score: team.score,
                    teamMembers: team.teamMembers,
                    usersCount: team.usersCount,
                    profilePicture: team.profilePicture,
                    changeOpenMode: () => onChangeOpenMode(team.uuid, teamType)
                },
                teamType === 'all' ? i : -1,
                membersNumberText,
                outOfText,
                tooltipText,
                true
            ),
            innerTable: (
                <InnerTableContainer isOpen={teamsUsersOpenState[`${team.uuid}-${teamType}`]} teamUuid={team.uuid} />
            ),
            isCollapseAble: true,
            isOpen: teamsUsersOpenState[`${team.uuid}-${teamType}`]
        }));

    useEffect(() => {
        setIsPreparingData(true);
        setAllTeamsRows(mapTeam(teams, 'all'));
        setIsPreparingData(false);
    }, [teams, teamsUsersOpenState]);

    useEffect(() => {
        setIsPreparingData(true);
        setMyTeamsRows(mapTeam(myTeams, 'my'));
        setIsPreparingData(false);
    }, [myTeams, teamsUsersOpenState]);

    const myTeamsExists = myTeamsRows.length > 0;

    return (
        <>
            {myTeamsRows.length > 0 && (
                <AccordionTable
                    rows={myTeamsRows}
                    title={myTeamsText}
                    isOpen={true}
                    hasMoreRows={hasMore}
                    loadMoreRows={onLoadMore}
                    tableHeight={allTeamsIsOpen ? '10rem' : '24.1rem'}
                    titleCount={myTeams.length}
                    headers={headers}
                    setIsOpen={(isOpen: boolean) => setMyTeamsIsOpen(isOpen)}
                    cellsWidths={['13%', '28%', '28%', '28%']}
                    noBottomBorderRadius
                />
            )}

            {allTeamsRows.length > 0 && (
                <AccordionTable
                    rows={allTeamsRows}
                    title={allTeamsText}
                    isOpen={true}
                    hasMoreRows={hasMore}
                    loadMoreRows={onLoadMore}
                    tableHeight={myTeamsExists && myTeamsIsOpen ? '10rem' : '24.1rem'}
                    setIsOpen={(isOpen: boolean) => setAllTeamsIsOpen(isOpen)}
                    headers={!myTeamsExists ? headers : undefined}
                    titleCount={allTeamsRows.length}
                    cellsWidths={['13%', '28%', '28%', '28%']}
                    noTopBorderRadius={myTeamsExists}
                />
            )}
            {teams.length === 0 && myTeams.length === 0 && !isPreparingData && (
                <div className={styles['empty-leaderboard']}>
                    <div className={styles.title}>{translate('NoOneTookAChallengeYet')}</div>
                    <div className={styles['sub-title']}>
                        {translate('TakeAnyChallengeFromTheCatalogToShowOnFirstPlace')}
                    </div>
                    <img
                        className={styles.image}
                        src={`${process.env.PUBLIC_URL}/images/challengeBackground.svg`}
                        alt="challenge"
                    />
                </div>
            )}
        </>
    );
};

interface TeamLeaderboardProps {
    teams: TeamData[];
    myTeams: TeamData[];
    onLoadMore: Function;
    hasMore: boolean;
    teamsUsersOpenState: { [teamUuid: string]: boolean };
    onChangeOpenMode: Function;
}

export default TeamLeaderboard;
